import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError, getToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { CustomerShell } from "@/components/CustomerShell";
import { CareShell } from "@/components/CareShell";
import { useMode } from "@/context/ModeContext";
import { LanguageToggle } from "@/components/LanguageToggle";
import { ModeSwitch } from "@/components/ModeSwitch";
import { whatsappLink } from "@/lib/constants";
import { toast } from "sonner";
import { Phone, Mail, IdCard, LogOut, MessageCircle, Bell, ChevronRight, Languages, Scale, Pencil, Camera, Loader2, X, HeartHandshake, Gift, Volume2, VolumeX, Users, Play, Undo2, Headphones, Building2 } from "lucide-react";
import { isMuted, setMuted, getVolume, setVolume, previewChime } from "@/lib/sound";
import { DeleteAccount } from "@/components/DeleteAccount";
import { PushToggle } from "@/components/PushToggle";

const avatarSrc = (u) => (u?.avatar ? `${u.avatar}?auth=${getToken()}` : null);

export default function Profile() {
    const { user, logout, refresh } = useAuth();
    const { t } = useLang();
    const { mode } = useMode();
    const care = mode === "care";
    const accent = care ? "#F43F5E" : "#009C95";
    const Shell = care ? CareShell : CustomerShell;
    const tile = { backgroundColor: `${accent}1a`, color: accent };
    const [showEdit, setShowEdit] = useState(false);
    const [mySoc, setMySoc] = useState([]);
    const nav = useNavigate();
    useEffect(() => { if (user?.role === "customer") { api.get("/society/mine").then((r) => setMySoc(r.data || [])).catch(() => {}); } }, [user?.role]);

    const doLogout = async () => { await logout(); nav("/"); };

    return (
        <Shell>
            <div className="rounded-b-3xl px-5 pb-6 pt-6 text-white" style={{ backgroundColor: care ? accent : "#09233B" }}>
                <div className="flex items-center gap-4">
                    <button data-testid="profile-avatar-btn" onClick={() => setShowEdit(true)} className="relative">
                        {avatarSrc(user) ? (
                            <img src={avatarSrc(user)} alt="avatar" className="h-16 w-16 rounded-full object-cover ring-2 ring-white/20" />
                        ) : (
                            <div className="flex h-16 w-16 items-center justify-center rounded-full text-2xl font-extrabold text-white ring-2 ring-white/25" style={{ backgroundColor: care ? "#09233B" : "#009C95" }}>{user?.name?.[0] || "H"}</div>
                        )}
                        <span className="absolute -bottom-0.5 -right-0.5 rounded-full bg-white p-1 text-navy shadow"><Camera size={12} /></span>
                    </button>
                    <div className="flex-1">
                        <h1 className="font-heading text-xl font-extrabold">{user?.name}</h1>
                        <p className="text-sm text-white/60">{user?.customer_id || user?.phone}</p>
                    </div>
                    <button data-testid="profile-edit-btn" onClick={() => setShowEdit(true)} className="rounded-full bg-white/10 p-2.5 transition-colors hover:bg-white/20"><Pencil size={16} /></button>
                </div>
            </div>
            {showEdit && <EditProfile user={user} onClose={() => setShowEdit(false)} onSaved={async () => { await refresh(); setShowEdit(false); }} />}

            <div className="space-y-4 px-5 py-5">
                <div className="flex items-center justify-between rounded-2xl border border-navy/10 bg-white p-4">
                    <div className="flex items-center gap-2"><HeartHandshake size={17} className="text-[#F43F5E]" /><span className="text-sm font-semibold text-navy">App mode</span></div>
                    <ModeSwitch />
                </div>
                <div className="flex items-center justify-between rounded-2xl border border-navy/10 bg-white p-4">
                    <div className="flex items-center gap-2"><Languages size={17} className="text-navy/40" /><span className="text-sm font-semibold text-navy">{t("Language")}</span></div>
                    <LanguageToggle />
                </div>

                <SoundSettings accent={accent} />
                <PushToggle accent={accent} />

                <button data-testid="profile-family-link" onClick={() => nav("/care/family")} className="flex w-full items-center gap-3 rounded-2xl border border-navy/10 bg-white p-4 text-left transition-transform active:scale-[0.98]">
                    <div className="rounded-xl p-2.5" style={{ backgroundColor: "#F43F5E1a", color: "#F43F5E" }}><Users size={20} /></div>
                    <div className="flex-1"><h3 className="font-heading font-bold text-navy">Family &amp; Guardians</h3><p className="text-xs text-navy/50">{user?.guardian_of ? "Manage your linked family member" : "Let family help manage your Care"}</p></div>
                    <ChevronRight size={18} className="text-navy/30" />
                </button>
                <div className="rounded-2xl border border-navy/10 bg-white p-4">
                    <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-navy/40">{t("Account")}</h3>
                    <div className="space-y-3">
                        <Info icon={IdCard} label="Customer ID" value={user?.customer_id} />
                        <Info icon={Phone} label="Mobile" value={user?.phone} />
                        {user?.email && <Info icon={Mail} label="Email" value={user.email} />}
                    </div>
                </div>

                <button data-testid="profile-notifications-link" onClick={() => nav("/notifications")} className="flex w-full items-center gap-3 rounded-2xl border border-navy/10 bg-white p-4 text-left transition-transform active:scale-[0.98]">
                    <div className="rounded-xl p-2.5" style={tile}><Bell size={20} /></div>
                    <div className="flex-1"><h3 className="font-heading font-bold text-navy">{t("Notifications")}</h3><p className="text-xs text-navy/50">View all your alerts & updates</p></div>
                    <ChevronRight size={18} className="text-navy/30" />
                </button>

                <button data-testid="profile-help-link" onClick={() => nav("/help")} className="flex w-full items-center gap-3 rounded-2xl border border-teal/30 bg-teal/5 p-4 text-left transition-transform active:scale-[0.98]">
                    <div className="rounded-xl p-2.5" style={tile}><Headphones size={20} /></div>
                    <div className="flex-1"><h3 className="font-heading font-bold text-navy">Helpdesk</h3><p className="text-xs text-navy/50">Call, WhatsApp or raise a ticket</p></div>
                    <ChevronRight size={18} className="text-navy/30" />
                </button>

                <button data-testid="profile-refunds-link" onClick={() => nav("/refunds")} className="flex w-full items-center gap-3 rounded-2xl border border-navy/10 bg-white p-4 text-left transition-transform active:scale-[0.98]">
                    <div className="rounded-xl p-2.5" style={tile}><Undo2 size={20} /></div>
                    <div className="flex-1"><h3 className="font-heading font-bold text-navy">My refunds</h3><p className="text-xs text-navy/50">Track refund status step by step</p></div>
                    <ChevronRight size={18} className="text-navy/30" />
                </button>

                {mySoc.length > 0 && (
                    <button data-testid="profile-my-society-link" onClick={() => nav("/my-society")} className="flex w-full items-center gap-3 rounded-2xl border border-orange/30 bg-orange/5 p-4 text-left transition-transform active:scale-[0.98]">
                        <div className="rounded-xl bg-orange/10 p-2.5 text-orange"><Building2 size={20} /></div>
                        <div className="flex-1"><h3 className="font-heading font-bold text-navy">My Society</h3><p className="text-xs text-navy/50">{mySoc[0].name} · {mySoc.reduce((a, s) => a + s.enrolled, 0)} enrolled · ₹{mySoc.reduce((a, s) => a + s.reward_due, 0)} balance</p></div>
                        <ChevronRight size={18} className="text-navy/30" />
                    </button>
                )}

                <button data-testid="profile-referral-link" onClick={() => nav("/referral")} className="flex w-full items-center gap-3 rounded-2xl border border-navy/10 bg-white p-4 text-left transition-transform active:scale-[0.98]">
                    <div className="rounded-xl p-2.5" style={tile}><Gift size={20} /></div>
                    <div className="flex-1"><h3 className="font-heading font-bold text-navy">Refer & Earn ₹50</h3><p className="text-xs text-navy/50">Invite friends — cash to your bank</p></div>
                    <ChevronRight size={18} className="text-navy/30" />
                </button>

                <a href={whatsappLink()} target="_blank" rel="noreferrer" data-testid="profile-whatsapp-btn" className="flex items-center gap-3 rounded-2xl border border-navy/10 bg-white p-4 transition-transform active:scale-[0.98]">
                    <div className="rounded-xl bg-emerald-50 p-2.5"><MessageCircle size={20} className="text-emerald-600" /></div>
                    <div className="flex-1"><h3 className="font-heading font-bold text-navy">{t("WhatsApp Support")}</h3><p className="text-xs text-navy/50">{t("Chat with the HOMIIE team")}</p></div>
                    <ChevronRight size={18} className="text-navy/30" />
                </a>

                <button onClick={() => nav("/my-requests")} data-testid="my-requests-link" className="flex w-full items-center gap-3 rounded-2xl border border-navy/10 bg-white p-4 text-left transition-transform active:scale-[0.98]">
                    <div className="rounded-xl p-2.5" style={tile}><Bell size={20} /></div>
                    <div className="flex-1"><h3 className="font-heading font-bold text-navy">{t("My Service Requests")}</h3><p className="text-xs text-navy/50">{t("Coming-soon services you've requested")}</p></div>
                    <ChevronRight size={18} className="text-navy/30" />
                </button>

                <button onClick={() => nav(care ? "/wallet" : "/payments")} data-testid="profile-payments-link" className="flex w-full items-center gap-3 rounded-2xl border border-navy/10 bg-white p-4 text-left transition-transform active:scale-[0.98]">
                    <div className="rounded-xl p-2.5" style={tile}><IdCard size={20} /></div>
                    <div className="flex-1"><h3 className="font-heading font-bold text-navy">{care ? "Care Wallet" : t("Payments")}</h3><p className="text-xs text-navy/50">{care ? "Balance, recharge & history" : t("Your payments & invoices")}</p></div>
                    <ChevronRight size={18} className="text-navy/30" />
                </button>

                <button onClick={() => nav("/legal")} data-testid="profile-legal-link" className="flex w-full items-center gap-3 rounded-2xl border border-navy/10 bg-white p-4 text-left transition-transform active:scale-[0.98]">
                    <div className="rounded-xl p-2.5" style={tile}><Scale size={20} /></div>
                    <div className="flex-1"><h3 className="font-heading font-bold text-navy">{t("Legal & Policies")}</h3><p className="text-xs text-navy/50">{t("Terms, Privacy, Payment, Refund & CareBack")}</p></div>
                    <ChevronRight size={18} className="text-navy/30" />
                </button>

                <DeleteAccount />

                <button data-testid="logout-btn" onClick={doLogout} className="flex w-full items-center justify-center gap-2 rounded-full border border-red-200 py-3 text-sm font-bold text-red-600 transition-colors hover:bg-red-50">
                    <LogOut size={17} /> {t("LOG OUT")}
                </button>
                <p className="pt-2 text-center text-[11px] text-navy/35">HOMIIE · Your Home, Managed.</p>
            </div>
        </Shell>
    );
}

function Info({ icon: Icon, label, value }) {
    return (
        <div className="flex items-center gap-3">
            <Icon size={17} className="text-navy/40" />
            <div><p className="text-xs text-navy/45">{label}</p><p className="text-sm font-semibold text-navy">{value || "—"}</p></div>
        </div>
    );
}

function SoundSettings({ accent }) {
    const [svc, setSvc] = useState(!isMuted("services"));
    const [care, setCare] = useState(!isMuted("care"));
    const [vol, setVol] = useState(Math.round(getVolume() * 100));

    const toggleSvc = () => { const on = !svc; setSvc(on); setMuted("services", !on); if (on) previewChime("customer"); };
    const toggleCare = () => { const on = !care; setCare(on); setMuted("care", !on); if (on) previewChime("care"); };
    const changeVol = (v) => { setVol(v); setVolume(v); };

    const Toggle = ({ on, onClick, testid }) => (
        <button data-testid={testid} onClick={onClick} className={`relative h-6 w-11 rounded-full transition-colors ${on ? "" : "bg-navy/20"}`} style={on ? { backgroundColor: accent } : {}}>
            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${on ? "left-[22px]" : "left-0.5"}`} />
        </button>
    );

    return (
        <div className="rounded-2xl border border-navy/10 bg-white p-4" data-testid="sound-settings">
            <div className="mb-3 flex items-center gap-2">
                {(svc || care) ? <Volume2 size={17} className="text-navy/40" /> : <VolumeX size={17} className="text-navy/40" />}
                <h3 className="text-xs font-semibold uppercase tracking-wide text-navy/40">Notification sounds</h3>
            </div>
            <div className="space-y-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-navy">Services alerts</span>
                        <button onClick={() => previewChime("customer")} className="rounded-md p-1 text-navy/40 hover:text-navy" data-testid="test-services-sound"><Play size={13} /></button>
                    </div>
                    <Toggle on={svc} onClick={toggleSvc} testid="sound-toggle-services" />
                </div>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-navy">Care alerts</span>
                        <button onClick={() => previewChime("care")} className="rounded-md p-1 text-navy/40 hover:text-[#F43F5E]" data-testid="test-care-sound"><Play size={13} /></button>
                    </div>
                    <Toggle on={care} onClick={toggleCare} testid="sound-toggle-care" />
                </div>
                <div className="pt-1">
                    <div className="mb-1 flex items-center justify-between"><span className="text-xs font-semibold text-navy/55">Volume</span><span className="text-xs text-navy/40">{vol}%</span></div>
                    <input data-testid="sound-volume-slider" type="range" min="0" max="100" value={vol} onChange={(e) => changeVol(parseInt(e.target.value, 10))} className="w-full accent-[#F43F5E]" style={{ accentColor: accent }} />
                </div>
            </div>
        </div>
    );
}


function EditProfile({ user, onClose, onSaved }) {
    const [name, setName] = useState(user?.name || "");
    const [avatar, setAvatar] = useState(user?.avatar || null);
    const [busy, setBusy] = useState(false);
    const [uploading, setUploading] = useState(false);

    const upload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith("image/")) return toast.error("Please choose an image file");
        if (file.size > 8 * 1024 * 1024) return toast.error("Photo must be under 8 MB");
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setAvatar(data.url);
        } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setUploading(false); }
    };

    const save = async () => {
        if (!name.trim()) return toast.error("Name cannot be empty");
        setBusy(true);
        try {
            await api.patch("/me", { name: name.trim(), avatar });
            toast.success("Profile updated");
            await onSaved();
        } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const preview = avatar ? `${avatar}?auth=${getToken()}` : null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/40 p-4" onClick={onClose} data-testid="edit-profile-modal">
            <div className="w-full max-w-sm rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
                <div className="mb-4 flex items-center justify-between">
                    <h2 className="font-heading text-lg font-extrabold text-navy">Edit profile</h2>
                    <button data-testid="edit-profile-close" aria-label="Close" onClick={onClose}><X size={20} className="text-navy/50" /></button>
                </div>
                <div className="flex flex-col items-center">
                    <label className="relative cursor-pointer">
                        {preview ? (
                            <img src={preview} alt="avatar" className="h-20 w-20 rounded-full object-cover ring-2 ring-teal/20" />
                        ) : (
                            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-teal text-3xl font-extrabold text-white">{name?.[0] || "H"}</div>
                        )}
                        <span className="absolute -bottom-1 -right-1 rounded-full bg-teal p-1.5 text-white shadow">{uploading ? <Loader2 size={13} className="animate-spin" /> : <Camera size={13} />}</span>
                        <input type="file" accept="image/*" className="hidden" data-testid="avatar-input" onChange={upload} />
                    </label>
                    {avatar && <button data-testid="avatar-remove" onClick={() => setAvatar(null)} className="mt-2 text-xs font-semibold text-red-500">Remove photo</button>}
                </div>
                <label className="mt-5 mb-1.5 block text-sm font-semibold text-navy">Full name</label>
                <input data-testid="edit-name-input" aria-label="Full name" maxLength={80} value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-xl border border-border px-4 py-3 text-navy outline-none focus:border-teal" />
                <div className="mt-5 flex gap-2">
                    <button data-testid="cancel-profile-btn" onClick={onClose} className="flex-1 rounded-full border border-navy/15 py-3.5 text-sm font-bold text-navy">CANCEL</button>
                    <button data-testid="save-profile-btn" disabled={busy || uploading} onClick={save} className="flex flex-[2] items-center justify-center gap-2 rounded-full bg-teal py-3.5 text-sm font-bold text-white disabled:opacity-60">
                        {busy && <Loader2 className="h-4 w-4 animate-spin" />} SAVE
                    </button>
                </div>
            </div>
        </div>
    );
}
